import { useEffect, useState } from "react";
import { Sparkles, Heart, Activity, RefreshCw, FileText, Clock, ServerCrash } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "@/components/location-provider";
import { AiMarkdown } from "@/components/ai-markdown";
import { PageHeader } from "@/components/page-header";
import { useAiCard } from "@/lib/use-ai-card";
import { WeatherCalendar } from "@/components/insights/weather-calendar";
import { HistoricalComparison } from "@/components/insights/historical-comparison";

export default function InsightsPage() {
  const [summary, setSummary] = useState<object>({});
  const [daily, setDaily] = useState<unknown[]>([]);
  const [todayMax, setTodayMax] = useState<number | undefined>(undefined);
  const [todayMin, setTodayMin] = useState<number | undefined>(undefined);
  const [stage, setStage] = useState(0);
  const { current: location } = useLocation();

  useEffect(() => {
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => {
        const c = d.weather?.current;
        if (c) setSummary({
          temp: c.temp, feels_like: c.feels_like, humidity: c.humidity,
          wind_speed: c.wind_speed, uvi: c.uvi, weather: c.weather,
        });
        if (d.weather?.daily) {
          setDaily(d.weather.daily);
          const today = d.weather.daily[0];
          if (today?.temp) {
            setTodayMax(today.temp.max);
            setTodayMin(today.temp.min);
          }
        }
      });
  }, [location.lat, location.lon]);

  useEffect(() => {
    setStage(0);
    if (!Object.keys(summary).length) return;
    const t1 = setTimeout(() => setStage(1), 0);
    const t2 = setTimeout(() => setStage(2), 5000);
    const t3 = setTimeout(() => setStage(3), 10000);
    const t4 = setTimeout(() => setStage(4), 15000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [JSON.stringify(summary)]);

  const brief = useAiCard({ kind: "brief", location: location.name, summary, enabled: stage >= 1 });
  const highlights = useAiCard({ kind: "highlights", location: location.name, summary, enabled: stage >= 2 });
  const lifestyle = useAiCard({ kind: "lifestyle", location: location.name, summary, enabled: stage >= 3 });
  const health = useAiCard({ kind: "health", location: location.name, summary, enabled: stage >= 4 });

  const allCards = [brief, highlights, lifestyle, health];
  const anyRateLimit = allCards.some((c) => c.error === "rate_limit");
  const allDoneOrUnavailable = stage >= 4 && allCards.every((c) => !c.loading);
  const allFailed = allDoneOrUnavailable && allCards.every((c) => c.error || !c.text);

  if (anyRateLimit && allCards.every((c) => !c.text)) {
    return (
      <div className="space-y-5 animate-slide-up">
        <PageHeader eyebrow="AI Powered" title="Insights" subtitle={`Intelligence brief for ${location.name}`} />
        <UnavailablePanel onRetry={() => { brief.refresh(); highlights.refresh(); lifestyle.refresh(); health.refresh(); }} />
      </div>
    );
  }

  if (allFailed) {
    return (
      <div className="space-y-5 animate-slide-up">
        <PageHeader eyebrow="AI Powered" title="Insights" subtitle={`Intelligence brief for ${location.name}`} />
        <UnavailablePanel onRetry={() => { brief.refresh(); highlights.refresh(); lifestyle.refresh(); health.refresh(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="AI Powered"
        title="Insights"
        subtitle={`Intelligence brief for ${location.name}`}
        action={stage >= 4 && (
          <Badge variant="success" size="sm">Complete</Badge>
        )}
      />

      <WeatherCalendar daily={daily as never} />

      <HistoricalComparison todayMaxC={todayMax} todayMinC={todayMin} />

      <AiCard
        title="Operational Brief"
        icon={FileText}
        state={brief}
        stage={stage}
        required={1}
        featured
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <AiCard title="Highlights" icon={Sparkles} state={highlights} stage={stage} required={2} />
        <AiCard title="Lifestyle" icon={Activity} state={lifestyle} stage={stage} required={3} />
        <AiCard title="Health" icon={Heart} state={health} stage={stage} required={4} />
      </div>
    </div>
  );
}

function UnavailablePanel({ onRetry }: { onRetry: () => void }) {
  return (
    <GlassPanel intensity="medium">
      <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-primary/10 blur-2xl rounded-full" />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-border/40 bg-muted/40">
            <ServerCrash className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        <h2 className="text-xl font-bold tracking-tight">Temporarily unavailable</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          The AI service is at capacity. Insights will return shortly.
        </p>
        <button
          onClick={onRetry}
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-border/40 bg-card/60 backdrop-blur-sm px-4 py-2 text-sm font-medium hover:border-primary/40 hover:bg-card transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    </GlassPanel>
  );
}

interface CardProps {
  title: string;
  icon: typeof Sparkles;
  state: ReturnType<typeof useAiCard>;
  stage: number;
  required: number;
  featured?: boolean;
}

function AiCard({ title, icon: Icon, state, stage, required, featured }: CardProps) {
  const { text, loading, error, refresh } = state;
  const queued = stage < required;

  return (
    <GlassPanel intensity={featured ? "medium" : "low"} className={featured ? "!border-primary/20 relative" : ""}>
      {featured && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
      )}
      <div className="flex items-center justify-between px-4 pt-4 mb-2">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${featured ? "bg-primary/10" : "bg-muted/50"}`}>
            <Icon className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className={`font-semibold ${featured ? "text-base" : "text-sm"}`}>{title}</h3>
          {queued && <Badge variant="outline" size="sm">Queued</Badge>}
          {loading && <Badge variant="info" size="sm">Loading</Badge>}
        </div>
        <button onClick={refresh} disabled={loading || queued} className="rounded-lg p-1.5 hover:bg-muted/50 transition-colors disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="px-4 pb-4">
        {queued ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-6">
            <Clock className="h-3.5 w-3.5" />
            <span>Waiting for previous analysis to complete...</span>
          </div>
        ) : loading ? (
          <div className="space-y-2 py-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-3 w-3/5" />
            <Skeleton className="h-3 w-full" />
          </div>
        ) : error || !text ? (
          <p className="text-xs text-muted-foreground py-4">Temporarily unavailable.</p>
        ) : (
          <AiMarkdown text={text} />
        )}
      </div>
    </GlassPanel>
  );
}